/**
 * User profiles — read-only alias + avatar + cached email/displayName used to
 * render people names + initials. Fetched in batches from /me/profiles. The
 * server populates `email` + `displayName` from auth on /me hits, so even
 * users we've never seen locally get a real name instead of "User abc123".
 */

import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo } from 'react'
import { api } from '@/lib/api'

export interface PublicProfile {
  alias: string | null
  avatarUrl: string | null
  email: string | null
  displayName: string | null
}

const EMPTY_PROFILES: Record<string, PublicProfile> = {}

export function useProfiles(userIds: readonly string[]): {
  profiles: Record<string, PublicProfile>
  isLoading: boolean
} {
  const ids = useMemo(() => [...new Set(userIds.filter(Boolean))].sort(), [userIds])
  const q = useQuery({
    queryKey: ['profiles', ids.join(',')],
    queryFn: () => api<Record<string, PublicProfile>>(`/api/v1/me/profiles?userIds=${encodeURIComponent(ids.join(','))}`),
    enabled: ids.length > 0,
    staleTime: 60_000,
  })
  useEffect(() => {
    /* hook required for stable ref semantics across React Query versions */
  }, [ids])
  // CRITICAL: never return a fresh `{}` here. A new object identity on every
  // render makes any downstream `useMemo([..., profiles.profiles])` recompute
  // on every render, which in turn re-keys TipTap's `useEditor` deps and
  // creates an infinite re-mount loop (React error #185).
  return { profiles: q.data ?? EMPTY_PROFILES, isLoading: q.isLoading }
}

export interface DisplayPerson {
  id: string
  /** Best display name: profile.alias ?? auth.displayName ?? email-local. */
  displayName: string
  /** Best avatar url: profile.customAvatarUrl ?? auth.avatarUrl. */
  avatarUrl: string | null
  /** Image candidates in precedence order (custom, then auth provider); Gravatar is added by UserAvatar. */
  avatarSources: (string | null)[]
  /** Best known email — useful for @-mention matchers and tooltips. */
  email: string | null
}

export function pickDisplay(
  id: string,
  profile: PublicProfile | undefined,
  fallback?: { displayName?: string | null; email?: string; avatarUrl?: string | null },
): DisplayPerson {
  const alias = profile?.alias ?? null
  const avatar = profile?.avatarUrl ?? null
  const email = profile?.email ?? fallback?.email ?? null
  const authName = profile?.displayName ?? fallback?.displayName ?? null
  const display =
    alias ??
    authName ??
    (email ? email.split('@')[0] : `User ${id.slice(0, 6)}`)
  return {
    id,
    displayName: display,
    avatarUrl: avatar ?? fallback?.avatarUrl ?? null,
    avatarSources: [avatar, fallback?.avatarUrl ?? null],
    email,
  }
}